// THE REVIEW INDICATOR — what is happening to the features, on every tab that
// has a stake in it.
//
// The traceability chain's blind spot was never the generation; it was the
// silence around it. Attaching a file mints a materials version and schedules a
// delta review that can take ten minutes on the fleet, and until it lands the
// product says nothing — so "the review is queued", "a worker is on it", "the
// worker died" and "this page simply has not changed" all look identical.
//
// The strip closes that. It is mounted ONCE, at the page shell above the tab
// content, so Materials and Features read the same state from the same fetch
// rather than each growing their own half-answer. The two tabs already had a
// running-only amber banner apiece; those said nothing about a queued review and
// nothing at all about a failed one, which is exactly the case a person needs to
// see.
//
// STATE COMES FROM THE SERVER, not from arithmetic here. /api/docs/review_status
// derives it from requirement_job plus the two version ledgers — the same rows
// the rest of the chain is built on — so the indicator cannot disagree with the
// job it is describing.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, Clock, RefreshCw, Eye } from "lucide-react";
import { fetchData } from "../../fetchapi/fetchapi";

// "4 min", "2 h", "just now" — the strip's whole job is to make waiting legible,
// and an absolute timestamp makes the reader do the subtraction. There is no
// shared relative-time helper in the codebase yet; this is deliberately the
// smallest one that reads well rather than a general-purpose one.
export function ago(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 45) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ${m % 60 ? `${m % 60} min` : ""}`.trim();
  return `${Math.floor(h / 24)} d`;
}

// One tone vocabulary, matching the page's existing severity tokens and pill
// colours so the strip reads as part of Docs rather than a bolted-on widget.
const TONES = {
  preview:   { bg: "var(--severity-info-bg,#e0f2fe)",    fg: "var(--severity-info-text,#075985)" },
  building:  { bg: "var(--severity-info-bg,#e0f2fe)",    fg: "var(--severity-info-text,#075985)" },
  analysis_available: { bg: "rgba(16,185,129,.12)",      fg: "var(--severity-ok-text,#047857)" },
  queued:    { bg: "var(--severity-info-bg,#e0f2fe)",    fg: "var(--severity-info-text,#075985)" },
  scheduled: { bg: "var(--severity-info-bg,#e0f2fe)",    fg: "var(--severity-info-text,#075985)" },
  running:   { bg: "var(--severity-warning-bg,#fef3c7)", fg: "var(--severity-warning-text,#92400e)" },
  done:      { bg: "rgba(16,185,129,.12)",               fg: "var(--severity-ok-text,#047857)" },
  failed:    { bg: "var(--severity-error-bg,#fee2e2)",   fg: "var(--severity-error-text,#991b1b)" },
};

const BTN = "inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border "
  + "border-current/30 bg-transparent cursor-pointer hover:bg-black/5 dark:hover:bg-white/10 "
  + "disabled:opacity-50";

export default function ReviewStatusStrip({ projectSlug, jobs, onJobsChange, onOpenFeatures }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  // A tick purely so relative times age on screen while a review is queued —
  // "waiting 1 min" that stays 1 min for ten minutes is worse than no number.
  const [, setTick] = useState(0);
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      const r = await fetchData("docs/review_status", { method: "GET" });
      if (alive.current) setSt(r?.ok ? r : null);
    } catch {
      // A strip that cannot read its own state says nothing rather than
      // asserting a state it does not have.
      if (alive.current) setSt(null);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    setSt(null);
    load();
    return () => { alive.current = false; };
  }, [load, projectSlug]);

  // Re-read whenever the page's job list moves — that is the same signal the
  // tabs use, so the strip settles at the moment they do.
  useEffect(() => { load(); }, [load, jobs]);

  const state = st?.state || null;
  // "building" polls too: the strip is what tells the person the build landed
  // and the analysis opened, without a reload.
  const live = state === "running" || state === "queued" || state === "scheduled" || state === "building"
    || (state === "preview" && st?.detail?.preview === "generating");

  // Poll only while something is in flight, on the page's own 4s cadence. A
  // settled review costs nothing to display, so nothing is spent watching it.
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => { load(); setTick((n) => n + 1); }, 4000);
    return () => clearInterval(t);
  }, [live, load]);

  // A failed analysis that needs no retry (booked by mistake, superseded by a
  // later answer) used to sit on every Docs tab with Retry as the only way out.
  const dismiss = useCallback(async () => {
    setBusy(true); setNote("");
    try {
      await fetchData("docs/features/review/dismiss", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
      onJobsChange?.();
      load();
    } catch (e) {
      setNote(String(e?.message || "Could not dismiss."));
    } finally { setBusy(false); }
  }, [onJobsChange, load]);

  const retry = useCallback(async () => {
    setBusy(true); setNote("");
    try {
      // The SAME endpoint the manual button uses, and idempotent server-side:
      // an in-flight review is reported back rather than duplicated, so a person
      // hammering Retry cannot put two workers on one materials version.
      const r = await fetchData("docs/features/review", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: "{}",
      });
      setNote(r?.already_running
        ? "An analysis is already running — watching that one."
        : "Analysis booked.");
      onJobsChange?.();
      load();
    } catch (e) {
      setNote(String(e?.message || "Could not book the analysis."));
    } finally { setBusy(false); }
  }, [onJobsChange, load]);

  // Nothing to say: no review has ever run and none is owed.
  if (!state || state === "idle") return null;

  const tone = TONES[state] || TONES.queued;
  const mv = st.materials_version;
  const fv = st.features_version;
  const d = st.detail || {};

  let Icon = Clock;
  let body = null;
  const boardHref = `/p/${encodeURIComponent(projectSlug)}/b/main/board`;
  if (state === "preview") {
    // PREVIEW FIRST: variants of the change are designed and wait for an
    // approval; the build, then the analysis, follow it.
    const ps = d.preview;
    Icon = ps === "generating" ? Loader2 : ps === "failed" ? AlertTriangle : Eye;
    body = ps === "generating"
      ? <>Designing build previews for sources v{mv}… approve a variant on <b>Requirement sources</b> to start the build.</>
      : ps === "failed"
        ? <><span className="font-medium">The build preview could not be designed.</span> {d.error || ""} Retry on <b>Requirement sources</b>, or build without a preview.</>
        : <>Build previews for sources v{mv} are ready{d.rounds > 1 ? ` (${d.rounds} rounds)` : ""} — approve a variant on <b>Requirement sources</b> to start the build; the requirements analysis opens after it.</>;
  } else if (state === "building") {
    // HEURISTIC FIRST: the sources are being BUILT, not reviewed. The analysis
    // waits for this card.
    Icon = d.status === "queued" ? Clock : Loader2;
    body = (
      <>
        Building the project from sources v{mv} —{" "}
        <a href={boardHref} className="underline underline-offset-2 text-inherit" data-testid="review-strip-build">
          build #{d.request_id}
        </a>{" "}
        is {d.status}{d.since ? ` (${ago(d.since)})` : ""}. The requirements analysis opens when it is done.
      </>
    );
  } else if (state === "analysis_available") {
    Icon = CheckCircle2;
    // Since the preview runs once (2026-09-04), later sources wait for the
    // REVIEW, not a preview — and the review is a delta on what was added.
    const bv = d.build_version || mv;
    body = (
      <>
        {d.build === "built"
          ? <>Build #{d.request_id} from sources v{bv} is done.</>
          : d.build === "build_failed" || d.build === "build_cancelled"
            ? <>Build #{d.request_id} from sources v{bv} {d.build_status} — see the Board.</>
            : null}{" "}
        {st.reviewed_materials_version != null
          ? <>Sources v{mv} are newer than the requirements (reviewed at v{st.reviewed_materials_version}){d.preview === "not_needed" ? " — no preview, the shape is settled" : ""}. Press <b>Analyse</b> to review ONLY what was added since; nothing is regenerated.</>
          : <>Press <b>Analyse</b> to derive the requirements from sources v{mv} and see what is covered.</>}
      </>
    );
  } else if (state === "scheduled") {
    Icon = Clock;
    body = <>Analysis scheduled for sources v{mv} — waiting to start{d.since ? ` (${ago(d.since)})` : ""}.</>;
  } else if (state === "queued") {
    Icon = Clock;
    body = (
      <>
        {d.stage === "coverage"
          ? <>Coverage round {d.round}{d.passages != null ? ` (${d.passages} passage(s) of the sources no requirement links yet)` : ""} queued — waiting for a worker</>
          : <>Analysis queued for sources v{mv} — waiting for a worker</>}
        {d.since ? ` (${ago(d.since)})` : ""}
        {d.attempts > 1 ? ` · retry ${d.attempts}` : ""}.
      </>
    );
  } else if (state === "running" && d.stage === "coverage") {
    // The review's second act: every passage of the sources must be linked
    // to a requirement — the rounds go back for the ones the list missed.
    Icon = Loader2;
    body = (
      <>
        Linking the passages of the sources no requirement covers yet — round {d.round}
        {d.passages != null ? `, ${d.passages} passage(s)` : ""}
        {d.agent ? <> — claimed by <span className="font-medium">{d.agent}</span></> : null}. Text that asks for nothing is filed as <b>not a requirement</b>; nothing in the sources is left unread.
      </>
    );
  } else if (state === "running" && st.job?.review?.mode === "selection") {
    Icon = Loader2;
    body = (
      <>
        Answering a question about a passage of the sources
        {d.agent ? <> — claimed by <span className="font-medium">{d.agent}</span></> : null}. The answer appears under the passage on <b>Requirement sources</b>; only missing requirements are added.
      </>
    );
  } else if (state === "running") {
    Icon = Loader2;
    body = (
      <>
        {st.reviewed_materials_version != null ? <>Reviewing what changed in sources v{mv} since v{st.reviewed_materials_version}</> : <>Analysing sources v{mv}</>}
        {d.agent ? <> — claimed by <span className="font-medium">{d.agent}</span></> : null}
        {/* "just now" is already a complete phrase; only an elapsed span takes
            the "ago" suffix, or the strip reads "just now ago". */}
        {d.claimed_at ? (ago(d.claimed_at) === "just now" ? " just now" : ` ${ago(d.claimed_at)} ago`) : ""}
        {d.attempts > 1 ? ` · retry ${d.attempts}` : ""}. Overrides, tasks and
        anything you edited are kept.
      </>
    );
  } else if (state === "done") {
    Icon = CheckCircle2;
    const res = st.job?.result || {};
    body = (
      <>
        {res.mode === "selection"
          ? <>Requirements v{fv}: a passage of the sources was checked — {res.verdict === "covered" ? "already covered" : res.verdict === "gap" ? `a gap, ${res.added || 0} requirement(s) added` : res.verdict === "partially_covered" ? `partly covered, ${res.added || 0} added` : "it asks nothing of the project"}.</>
          : res.mode === "recheck"
            ? <>Requirements v{fv} re-checked against the implementation (sources v{st.reviewed_materials_version ?? mv} unchanged).</>
            : res.mode === "delta"
              ? <>Requirements v{fv} reviewed against what changed in sources v{st.reviewed_materials_version ?? mv}{res.added || res.reworked ? ` — ${res.added || 0} added, ${res.reworked || 0} updated` : ""}.</>
              : res.mode === "coverage"
                ? <>Requirements v{fv}: the passages of sources v{st.reviewed_materials_version ?? mv} no requirement covered were linked{res.added ? ` — ${res.added} row(s) added` : ""}.</>
                : <>Requirements v{fv} analysed against sources v{st.reviewed_materials_version ?? mv}.</>}
        {res.coverage && res.mode !== "selection" ? (
          <span data-testid="review-strip-coverage" data-coverage-complete={res.coverage.complete ? "true" : "false"}>
            {res.coverage.complete
              ? " Every passage of the sources is linked to a requirement."
              : ` ${res.coverage.passages_after} passage(s) of the sources are still not linked — press "Cover the rest" on Requirement sources.`}
          </span>
        ) : null}
        {onOpenFeatures ? (
          <button type="button" onClick={onOpenFeatures} data-testid="review-strip-open"
            className="underline underline-offset-2 bg-transparent border-0 p-0 ml-1 cursor-pointer text-inherit">
            See the requirements
          </button>
        ) : null}
      </>
    );
  } else if (state === "failed") {
    Icon = AlertTriangle;
    body = (
      <>
        <span className="font-medium">The requirements analysis did not finish.</span>{" "}
        {d.error || "No reason was recorded."}
        {d.attempts > 1 ? ` (after ${d.attempts} attempts)` : ""}
      </>
    );
  }

  return (
    <div
      className="shrink-0 flex items-start gap-2 text-xs px-3 py-2 border-b border-card-border"
      style={{ background: tone.bg, color: tone.fg }}
      data-testid="review-status-strip"
      data-review-state={state}
    >
      <Icon size={13} className={`shrink-0 mt-[1px] ${state === "running" || (state === "preview" && d.preview === "generating") ? "animate-spin" : ""}`} />
      <div className="flex-1 min-w-0">{body}</div>
      {note ? <span className="shrink-0 opacity-80">{note}</span> : null}
      {state === "failed" ? (
        <button type="button" onClick={dismiss} disabled={busy} className={`${BTN} shrink-0`}
          data-testid="review-strip-dismiss" title="Put this failure away — the last finished analysis stays as it is">
          Dismiss
        </button>
      ) : null}
      {(state === "failed" || state === "scheduled" || state === "analysis_available") ? (
        <button type="button" onClick={retry} disabled={busy} className={`${BTN} shrink-0`}
          data-testid="review-strip-retry">
          <RefreshCw size={11} className={busy ? "animate-spin" : ""} />
          {busy ? "Booking…" : state === "analysis_available" ? "Analyse" : "Retry"}
        </button>
      ) : null}
    </div>
  );
}
